import React, { useEffect, useRef, useState } from 'react';
import jwtAxios from '@projectK/services/auth/JWT';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';

const STORAGE_KEY = 'calendar_sync_watch';
const POLL_MS = 12000;
const WATCH_EVENT = 'calendar-sync-watch-started';
const WS_PATH = '/ws/calendar-sync-status';

const readWatchData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.provider || !parsed?.startedAt) return null;
    return parsed;
  } catch (error) {
    return null;
  }
};

const clearWatchData = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const CalendarSyncStatusPopup = () => {
  const { isAuthenticated, user } = useAuthUser();
  const timerRef = useRef(null);
  const wsRef = useRef(null);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogType, setDialogType] = useState('success');
  const [open, setOpen] = useState(false);
  const [watchData, setWatchData] = useState(null);

  const stopPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopWebSocket = () => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch (error) {
        // Ignore close errors.
      }
      wsRef.current = null;
    }
  };

  const pollStatus = async (activeWatchData) => {
    if (!activeWatchData || !isAuthenticated) return;

    try {
      const response = await jwtAxios.get('/events/sync/status');
      const rows = response?.data?.data || [];
      const row = rows.find(
        item =>
          String(item?.provider || '').toLowerCase() ===
          String(activeWatchData.provider || '').toLowerCase(),
      );
      if (!row) return;

      const completedAtMs = row?.last_sync_completed_at
        ? new Date(row.last_sync_completed_at).getTime()
        : 0;
      const startedAtMs = new Date(activeWatchData.startedAt).getTime();
      if (!completedAtMs || completedAtMs < startedAtMs) return;

      if (row?.sync_status === 'completed') {
        setDialogType('success');
        setDialogMessage('Events have been successfully synced to your calendar.');
        setOpen(true);
        clearWatchData();
        setWatchData(null);
        stopPolling();
        return;
      }

      if (row?.sync_status === 'failed') {
        setDialogType('error');
        setDialogMessage(
          'We experienced an issue while syncing your data. Please try again later.',
        );
        setOpen(true);
        clearWatchData();
        setWatchData(null);
        stopPolling();
      }
    } catch (error) {
      // Avoid noisy user-facing errors for background polling.
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      stopPolling();
      stopWebSocket();
      setWatchData(null);
      return;
    }
    if (!watchData) {
      stopPolling();
      stopWebSocket();
      return;
    }
    pollStatus(watchData);
    stopPolling();
    timerRef.current = setInterval(() => pollStatus(watchData), POLL_MS);
    return () => stopPolling();
  }, [isAuthenticated, watchData]);

  useEffect(() => {
    if (!isAuthenticated || !watchData) {
      stopWebSocket();
      return;
    }

    const currentUserId = user?.id || user?.userId;
    if (!currentUserId) return;

    const backendBaseUrl = process.env.NX_BACKEND_URL || window.location.origin;
    const backendUrl = new URL(backendBaseUrl);
    const wsProtocol = backendUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${backendUrl.host}${WS_PATH}?userId=${currentUserId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        if (data?.type !== 'calendar_sync_status') return;
        if (
          String(data?.provider || '').toLowerCase() !==
          String(watchData?.provider || '').toLowerCase()
        ) {
          return;
        }
        const completedAtMs = data?.last_sync_completed_at
          ? new Date(data.last_sync_completed_at).getTime()
          : 0;
        const startedAtMs = new Date(watchData.startedAt).getTime();
        if (!completedAtMs || completedAtMs < startedAtMs) return;

        if (data?.sync_status === 'completed') {
          setDialogType('success');
          setDialogMessage('Events have been successfully synced to your calendar.');
          setOpen(true);
          clearWatchData();
          setWatchData(null);
          stopPolling();
          stopWebSocket();
          return;
        }

        if (data?.sync_status === 'failed') {
          setDialogType('error');
          setDialogMessage(
            'We experienced an issue while syncing your data. Please try again later.',
          );
          setOpen(true);
          clearWatchData();
          setWatchData(null);
          stopPolling();
          stopWebSocket();
        }
      } catch (error) {
        // Ignore malformed payloads.
      }
    };

    return () => {
      if (wsRef.current === ws) {
        stopWebSocket();
      }
    };
  }, [isAuthenticated, watchData, user?.id, user?.userId]);

  useEffect(() => {
    const bootstrap = readWatchData();
    if (bootstrap) {
      setWatchData(bootstrap);
    }
  }, []);

  useEffect(() => {
    const onWatchStart = () => {
      const nextWatchData = readWatchData();
      if (!nextWatchData) return;
      setWatchData(nextWatchData);
      setDialogType('info');
      setDialogMessage(
        'Calendar sync started. You will get a popup when it completes.',
      );
      setOpen(true);
    };

    window.addEventListener(WATCH_EVENT, onWatchStart);
    return () => {
      window.removeEventListener(WATCH_EVENT, onWatchStart);
    };
  }, []);
  const diaogColor = (dialogType) => {
    let dialogColorMes = '#1e88e5';
    if (dialogType === 'success') {
      dialogColorMes = 'green';
    } else if (dialogType === 'error') {
      dialogColorMes = 'red';
    }
    return dialogColorMes;
  };
  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm'>
      <DialogTitle>
        <Typography variant='h4'>Sync Confirmation</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 'bold',
            color: diaogColor(dialogType),
          }}
        >
          {dialogMessage}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} color='primary'>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalendarSyncStatusPopup;
